import { initializeApp, getApp, getApps, type FirebaseApp } from "firebase/app";
import { 
  getAuth,
  GoogleAuthProvider, 
  signInWithPopup,
  signInWithCredential,
  type Auth
} from "firebase/auth";
import { getFirestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager, type Firestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { getAnalytics, logEvent, type Analytics } from "firebase/analytics";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "demo-api-key",
  authDomain: `${import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-project"}.firebaseapp.com`,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-project",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || `${import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-project"}.firebasestorage.app`,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "demo-app-id",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
const auth: Auth = getAuth(app);

// Initialize Analytics (only in browser environment)
let analytics: Analytics | null = null;
if (typeof window !== 'undefined') {
  analytics = getAnalytics(app);
}

// Initialize Firestore with error handling for IndexedDB issues
let db: Firestore;

// Try to initialize with persistence settings first
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager()
    })
  });
  if (import.meta.env.DEV) {
    console.debug('Firestore initialized with persistence');
  }
} catch (error: any) {
  // Check if already initialized (hot-reload) vs IndexedDB error
  if (error.message?.includes('already been called')) {
    // Hot-reload scenario - get existing instance
    if (import.meta.env.DEV) {
      console.debug('Firestore already initialized, using existing instance');
    }
  } else {
    // IndexedDB or other initialization error
    console.warn('Failed to initialize Firestore with persistence:', error.message);
  }
  
  // This handles both:
  // 1. Already initialized (during hot-reload) - returns existing instance
  // 2. IndexedDB unavailable (iOS private mode, etc.) - initializes with memory cache
  db = getFirestore(app);
}

export { app, auth, analytics, db };
export const storage = getStorage(app);
export const functions = getFunctions(app, "africa-south1");

// Analytics helper function
export const trackEvent = (eventName: string, eventParams?: Record<string, any>) => {
  if (analytics) {
    logEvent(analytics, eventName, eventParams);
  } else {
    console.warn('Analytics not initialized');
  }
};

const googleProvider = new GoogleAuthProvider();
export const signInWithGoogle = () => {
    return signInWithPopup(auth, googleProvider);
};

export const getGoogleCredential = (token: string) => {
    return GoogleAuthProvider.credential(token);
};

export const signInWithGoogleCredential = (credential: any) => {
    return signInWithCredential(auth, credential);
}
